import React, {
  Fragment,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { Box, Dialog, Radio, PaperProps, Divider } from '@mui/material';
import { IAppState } from 'spw-redux/reducer';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-v5';
import {
  saveUpdatingScenario,
  setIsEditingSavedScenario,
  setWorksheetUpdateDialogStatus,
} from 'spw-redux/modules/calculators/actions';
import {
  WorksheetUpdateDialogContent,
  BigBubbleIcon,
  CategoriesRow,
  SpaceCategoryTitle,
  WorksheetUpdateDialogTitle,
  WorksheetUpdateDialogRadioGroup as RadioGroup,
  WorksheetUpdateDialogFormControlLabel as FormControlLabel,
  WorksheetDialogParagraph,
  FixedBackdrop,
  WorksheetUpdateWorkSeatsTable,
  WorksheetDialogPaper,
} from 'spw-components/dialogs/styles';
import {
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MACRO_PROGRAMMING_TYPE,
  DEFAULT_SHARED_SPACES_FIELDS_TO_KEEP,
  WORKSHEET_VALUES,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import {
  Row,
  SpaceBetweenRow,
  SPWDialogActions,
  SPWMainButton,
  SPWCancelButton,
} from 'spw-shared/globalComponents';
import { IWorksheetRowValue } from 'spw-redux/modules/calculators/types';
import { colors } from 'spw-theme';
import {
  currentProject,
  projectColors as selectProjectColors,
  selectFirstBuilding,
} from 'spw-redux/modules/projects/selectors';
import { IProjectSpaceTypeCategory } from 'spw-redux/modules/projects/types';
import Draggable from 'react-draggable';
import {
  selectCurrentScenario,
  selectScenarioListType,
} from 'spw-redux/modules/calculators/selectors';
import { getColor, numberWithCommas } from 'spw-shared/utils';
import { getScenarioListLocation } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import { PlainProgramScenario } from '../../models/Scenario';
import { PlainWorksheet } from '../../models/Worksheet';

function PaperComponent(props: PaperProps) {
  return (
    <Draggable
      handle="#draggable-worksheet-update-dialog"
      bounds="#fixed-backdrop-draggable-bounds"
    >
      <WorksheetDialogPaper {...props} />
    </Draggable>
  );
}

interface ConfirmWorksheetUpdateProps {
  isShowingWorksheetUpdateDialog: boolean;
  scenario?: PlainProgramScenario;
  worksheet?: PlainWorksheet | null;
  projectSpaceTypeCategories?: IProjectSpaceTypeCategory[];
}

const getDeltaWorkSeatsColor = (workSeatsDelta: number): string | undefined => {
  if (workSeatsDelta > 0) return colors.mainGreen;
  if (workSeatsDelta < 0) return colors.red;
  return undefined;
};

const draggableDialogStyle = {
  style: {
    zIndex: 1300,
    left: '20px',
    position: 'fixed',
    margin: 0,
  },
} as PaperProps;

const MINIMUM_TOP_DISTANCE = 128;
const DIALOG_HEIGHT = 709;

const ConfirmWorksheetUpdate: React.FC<ConfirmWorksheetUpdateProps> = ({
  isShowingWorksheetUpdateDialog,
  scenario,
  worksheet,
  projectSpaceTypeCategories,
}: ConfirmWorksheetUpdateProps) => {
  const scenarioListType = useSelector(selectScenarioListType);
  const dispatch = useDispatch();
  const project = useSelector(currentProject);
  const location = useLocation();
  const history = useHistory();

  const redirectToProgramSummaryList = useCallback(() => {
    const scenarioListLocation = getScenarioListLocation(project, scenario);

    history.replace({
      pathname: scenarioListLocation.pathname.replace('building/', 'summary/'),
      search: scenarioListLocation.search,
    });
  }, [history, project, scenario]);

  const projectColors = useSelector(selectProjectColors);

  const showScenarioList = useCallback(() => {
    if (scenarioListType) {
      if (location.search?.indexOf('referrer=summary') !== -1) {
        redirectToProgramSummaryList();
      } else {
        history.replace(getScenarioListLocation(project, scenario));
      }
    }
  }, [
    scenarioListType,
    location,
    redirectToProgramSummaryList,
    history,
    project,
    scenario,
  ]);

  const [workspacesFieldToKeep, setWorkSpacesFieldToKeep] = useState<
    IWorksheetRowValue
  >(WORKSHEET_VALUES.TARGET_RATIO as IWorksheetRowValue);
  const [sharedSpacesFieldsToKeep, setSharedSpacesFieldsToKeep] = useState<
    typeof DEFAULT_SHARED_SPACES_FIELDS_TO_KEEP
  >(DEFAULT_SHARED_SPACES_FIELDS_TO_KEEP);

  const isMacroScenarioShared =
    scenario?.calculatorType === MACRO_PROGRAMMING_TYPE && scenario?.isShared;

  useLayoutEffect(() => {
    if (scenario?.areStoredWorkSeatsOutdated && worksheet?.id) {
      dispatch(setWorksheetUpdateDialogStatus(true));
    }
    return () => {
      dispatch(setWorksheetUpdateDialogStatus(false));
    };
  }, [scenario?.areStoredWorkSeatsOutdated, worksheet?.id, dispatch]);

  const { sharedCategories, individualWorkspacesCategory } = useMemo(() => {
    const otherCategories: IProjectSpaceTypeCategory[] = [];
    let individualCategory: IProjectSpaceTypeCategory;
    if (projectSpaceTypeCategories)
      projectSpaceTypeCategories.forEach((c) => {
        if (
          c.defaultSpaceTypeCategory.category === INDIVIDUAL_WORKSPACES_CATEGORY
        ) {
          individualCategory = c;
        } else {
          otherCategories.push(c);
        }
      });
    return {
      sharedCategories: otherCategories,
      individualWorkspacesCategory: individualCategory,
    };
  }, [projectSpaceTypeCategories]);

  const handleClose = useCallback(() => {
    dispatch(setWorksheetUpdateDialogStatus(false));
  }, [dispatch]);

  const handleUpdateClick = () => {
    dispatch(setIsEditingSavedScenario(false));
    dispatch(
      saveUpdatingScenario(
        false,
        false,
        isMacroScenarioShared ? null : sharedSpacesFieldsToKeep,
        workspacesFieldToKeep,
        () => {
          const wasUpdatedComingFromProgramSummaryList =
            location.search?.indexOf('referrer=summary') !== -1;
          if (wasUpdatedComingFromProgramSummaryList) {
            redirectToProgramSummaryList();
          }
        }
      )
    );

    handleClose();
  };

  const workSeatsDelta = scenario
    ? scenario.workSeats - scenario.storedWorkSeats
    : 0;

  const topPosition =
    window.innerHeight - (window.innerHeight - MINIMUM_TOP_DISTANCE);

  return (
    <>
      {isShowingWorksheetUpdateDialog && (
        <FixedBackdrop id="fixed-backdrop-draggable-bounds" />
      )}
      <Dialog
        open={isShowingWorksheetUpdateDialog}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-worksheet-update-dialog"
        disableEnforceFocus
        style={{ position: 'initial' }}
        hideBackdrop
        PaperProps={{
          ...draggableDialogStyle,
          style: {
            ...draggableDialogStyle.style,
            top: topPosition,
            maxHeight: Math.min(
              window.innerHeight - MINIMUM_TOP_DISTANCE,
              DIALOG_HEIGHT
            ),
          },
        }}
        classes={{
          container: 'worksheet-update-dialog-container',
        }}
        disablePortal
      >
        <WorksheetUpdateDialogContent>
          <div
            style={{ cursor: 'move' }}
            id="draggable-worksheet-update-dialog"
          >
            <WorksheetUpdateDialogTitle>
              Worksheet Updates
            </WorksheetUpdateDialogTitle>
            <WorksheetDialogParagraph className="first">
              The total number of work seats has changed. This requires the
              program worksheet to recalculate.
            </WorksheetDialogParagraph>
          </div>
          {scenario && (
            <WorksheetUpdateWorkSeatsTable>
              <SpaceBetweenRow className="no-margin-bottom">
                <p>Previous Work Seats</p>
                <p>{numberWithCommas(scenario.storedWorkSeats, false, true)}</p>
              </SpaceBetweenRow>
              <SpaceBetweenRow>
                <p>New Work Seats</p>
                <p>{numberWithCommas(scenario.workSeats, false, true)}</p>
              </SpaceBetweenRow>
              <Divider />
              <SpaceBetweenRow>
                <p>Delta</p>
                <p
                  style={{ color: getDeltaWorkSeatsColor(workSeatsDelta) }}
                  data-testid="worksheet_update_delta_work_seats"
                >
                  {workSeatsDelta > 0 ? '+' : ''}
                  {numberWithCommas(workSeatsDelta, false, true)}
                </p>
              </SpaceBetweenRow>
            </WorksheetUpdateWorkSeatsTable>
          )}
          <WorksheetDialogParagraph className="middle">
            Select the values below to remain constant. The values that are not
            selected will recalculate.
          </WorksheetDialogParagraph>
          <CategoriesRow style={{ marginBottom: '0' }}>
            <Box display="flex">
              <Box display="flex" alignItems="center">
                <BigBubbleIcon
                  color={getColor(
                    INDIVIDUAL_WORKSPACES_CATEGORY,
                    projectColors
                  )}
                />
              </Box>
              <SpaceCategoryTitle>
                {individualWorkspacesCategory?.name}
              </SpaceCategoryTitle>
            </Box>
          </CategoriesRow>
          <RadioGroup
            value={workspacesFieldToKeep}
            onChange={(event) => {
              setWorkSpacesFieldToKeep(
                event.target.value as IWorksheetRowValue
              );
            }}
            row
          >
            <FormControlLabel
              value={WORKSHEET_VALUES.TARGET_RATIO}
              control={<Radio size="small" />}
              label="Seat % Mix"
            />
            <FormControlLabel
              value={WORKSHEET_VALUES.QUANTITY_OF_SPACE}
              control={<Radio size="small" />}
              label="Quantity of Spaces"
              name="worksheet_update_preserve_iw_quantity"
            />
          </RadioGroup>
          {!isMacroScenarioShared && (
            <CategoriesRow style={{ marginBottom: '0px', maxWidth: 371 }}>
              {sharedCategories.map(
                (projectCategory: IProjectSpaceTypeCategory) => (
                  <Fragment
                    key={`row-${projectCategory.defaultSpaceTypeCategory.category}`}
                  >
                    <Row>
                      <Box display="flex" alignItems="center">
                        <BigBubbleIcon
                          color={getColor(
                            projectCategory.defaultSpaceTypeCategory.category,
                            projectColors
                          )}
                        />
                      </Box>
                      <SpaceCategoryTitle>
                        {projectCategory.name}
                      </SpaceCategoryTitle>
                    </Row>
                    <RadioGroup
                      className={
                        projectCategory.defaultSpaceTypeCategory.category ===
                        SUPPORT_SPACES_CATEGORY
                          ? 'last'
                          : undefined
                      }
                      value={
                        sharedSpacesFieldsToKeep[
                          projectCategory.defaultSpaceTypeCategory.category
                        ]
                      }
                      onChange={(event) => {
                        setSharedSpacesFieldsToKeep({
                          ...sharedSpacesFieldsToKeep,
                          [projectCategory.defaultSpaceTypeCategory
                            .category]: event.target
                            .value as IWorksheetRowValue,
                        });
                      }}
                      row
                    >
                      <FormControlLabel
                        value={WORKSHEET_VALUES.TARGET_RATIO}
                        control={<Radio size="small" />}
                        label="Target Ratios"
                      />
                      <FormControlLabel
                        value={WORKSHEET_VALUES.QUANTITY_OF_SPACE}
                        control={<Radio size="small" />}
                        label="Quantity of Spaces"
                        name={`worksheet_update_preserve_support_quantity_${projectCategory.defaultSpaceTypeCategory.category}`}
                      />
                    </RadioGroup>
                  </Fragment>
                )
              )}
            </CategoriesRow>
          )}
          <WorksheetDialogParagraph className="last">
            Holding ratios constant may create fractional qtys which can be
            edited after updating the worksheet.
          </WorksheetDialogParagraph>
        </WorksheetUpdateDialogContent>

        <SPWDialogActions style={{ paddingTop: '8px' }}>
          <SPWMainButton className="v-padded" onClick={handleUpdateClick}>
            UPDATE WORKSHEET
          </SPWMainButton>
          <SPWCancelButton
            customColor={colors.black}
            onClick={() => showScenarioList()}
          >
            CANCEL
          </SPWCancelButton>
        </SPWDialogActions>
      </Dialog>
    </>
  );
};

const mapStateToProps = (state: IAppState) => ({
  isShowingWorksheetUpdateDialog:
    state.calculatorReducer.showWorksheetUpdateDialog,
  scenario: selectCurrentScenario(state),
  currentBuilding: selectFirstBuilding(state),
  worksheet: state.calculatorReducer.worksheet,
  projectSpaceTypeCategories:
    state.projectsReducer.currentProject?.spaceTypeCategories,
  isEditingSavedScenario: state.calculatorReducer.isEditingSavedScenario,
});

export default connect(mapStateToProps)(ConfirmWorksheetUpdate);
